import React, { useState, useEffect } from 'react';
import { Menu, X, Globe } from 'lucide-react';
import { Language } from '../types';
import { CONTENT } from '../constants';

interface NavbarProps {
  lang: Language;
  setLang: (lang: Language) => void;
}

export const Navbar: React.FC<NavbarProps> = ({ lang, setLang }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const t = CONTENT[lang].nav;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLang = () => {
    setLang(lang === 'en' ? 'ar' : 'en');
  };

  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    setIsOpen(false);
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  const scrollToTop = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  return (
    <>
      <nav className={`fixed w-full z-50 transition-all duration-700 ease-out ${
        isScrolled 
          ? 'bg-luxury-paper/90 backdrop-blur-sm py-4 border-b border-stone-200' 
          : 'bg-transparent py-8'
      }`} dir={lang === 'ar' ? 'rtl' : 'ltr'}>
        <div className="max-w-[1920px] mx-auto px-6 md:px-12">
          <div className="flex justify-between items-center">
            
            {/* Left - Menu (Desktop) */}
            <div className="hidden md:flex items-center gap-8">
              <a 
                href="#services" 
                onClick={(e) => handleNavigation(e, 'services')}
                className={`text-xs font-medium uppercase tracking-[0.2em] transition-colors hover:text-greenline-800 ${isScrolled ? 'text-luxury-black' : 'text-white'}`}
              >
                {t.services}
              </a>
              <a 
                href="#about" 
                onClick={(e) => handleNavigation(e, 'about')}
                className={`text-xs font-medium uppercase tracking-[0.2em] transition-colors hover:text-greenline-800 ${isScrolled ? 'text-luxury-black' : 'text-white'}`}
              >
                {t.about}
              </a>
            </div>

            {/* Center - Logo */}
            <a href="#" onClick={scrollToTop} className="absolute left-1/2 transform -translate-x-1/2 text-center group cursor-pointer block">
              <img 
                src="/Picture1-1920w.webp" 
                alt="GreenLine Logistics" 
                className={`h-12 md:h-16 w-auto object-contain transition-all duration-700 ${isScrolled ? '' : 'brightness-0 invert'}`}
              />
            </a>

            {/* Right - Actions */}
            <div className="flex items-center gap-6 md:gap-8">
              <button 
                onClick={toggleLang}
                className={`text-xs font-medium uppercase tracking-widest transition-colors ${isScrolled ? 'text-luxury-black hover:text-greenline-800' : 'text-white hover:text-white/70'}`}
              >
                {lang === 'en' ? 'AR' : 'EN'}
              </button>
              
              <a 
                href="#contact" 
                onClick={(e) => handleNavigation(e, 'contact')}
                className={`hidden md:block text-xs font-medium uppercase tracking-[0.2em] border-b border-transparent hover:border-current transition-all pb-0.5 ${isScrolled ? 'text-luxury-black' : 'text-white'}`}
              >
                {t.contact}
              </a>

              <button 
                onClick={() => setIsOpen(!isOpen)}
                className={`md:hidden p-1 ${isScrolled ? 'text-luxury-black' : 'text-white'}`}
              >
                {isOpen ? <X size={24} strokeWidth={1} /> : <Menu size={24} strokeWidth={1} />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Full Screen Menu Overlay */}
      <div className={`fixed inset-0 bg-luxury-paper z-40 transition-transform duration-700 ease-[cubic-bezier(0.76,0,0.24,1)] ${isOpen ? 'translate-y-0' : '-translate-y-full'}`}>
         <div className="h-full flex flex-col items-center justify-center space-y-8">
            <a 
              href="#services" 
              onClick={(e) => handleNavigation(e, 'services')}
              className="font-serif text-5xl md:text-7xl text-luxury-black hover:italic hover:text-greenline-900 transition-all duration-300"
            >
              {t.services}
            </a>
            <a 
              href="#about" 
              onClick={(e) => handleNavigation(e, 'about')}
              className="font-serif text-5xl md:text-7xl text-luxury-black hover:italic hover:text-greenline-900 transition-all duration-300"
            >
              {t.about}
            </a>
            <a 
              href="#contact" 
              onClick={(e) => handleNavigation(e, 'contact')}
              className="font-serif text-5xl md:text-7xl text-luxury-black hover:italic hover:text-greenline-900 transition-all duration-300"
            >
              {t.contact}
            </a>
            <a 
              href="#" 
              onClick={(e) => { e.preventDefault(); setIsOpen(false); }}
              className="font-serif text-5xl md:text-7xl text-luxury-black/50 hover:text-luxury-black transition-all duration-300 cursor-not-allowed"
            >
              {t.portal}
            </a>
         </div>
      </div>
    </>
  );
};